import { useMemo } from 'react'
import { useCookies } from 'react-cookie'
import styled from 'styled-components'

import { Breadcrumb, Breadcrumbs } from '@syconium/little-miss-figgy/dist/components'
import { fromMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { cookieKeys } from '../../../../../_config/Cookies.config'
import { useTranslation } from '../../../../../_providers/TranslationProvider.client'
import { NextLink } from '../../../../navigation/NextLink'

import { ProductGroupCategory, breadcrumbData } from './constants'

function isProductGroupCategory(value: unknown): value is ProductGroupCategory {
  return Object.values(ProductGroupCategory).includes(value as ProductGroupCategory)
}

export interface UseBreadcrumbDataParams {
  genderCategory: string | undefined
  categoryId: string | undefined
}

export function useBreadcrumbData(params: UseBreadcrumbDataParams) {
  const { genderCategory, categoryId } = params
  const translations = useTranslation()
  const [cookies] = useCookies([cookieKeys.stylePreference.key])
  const navGenderCookie = cookies.navGender
  const breadcrumbTranslations = translations.pages.product.breadcrumbs

  return useMemo(() => {
    let gender: 'men' | 'women' | 'unisex'
    if (genderCategory === 'men' || genderCategory === 'women') {
      gender = genderCategory
    } else if (categoryId === ProductGroupCategory.GiftCard) {
      gender = 'unisex'
    } else if (navGenderCookie === 'women' || navGenderCookie === 'men') {
      gender = navGenderCookie
    } else {
      gender = 'unisex'
    }

    const { collection: genderLink, key: genderCategoryKey } = breadcrumbData[gender]
    const genderText = genderCategoryKey
      ? breadcrumbTranslations.gender[
          genderCategoryKey as keyof typeof breadcrumbTranslations.gender
        ]
      : undefined

    const category = isProductGroupCategory(categoryId) ? categoryId : undefined
    const categoryBreadcrumb = category ? breadcrumbData[gender].category[category] : undefined
    const categoryLink = categoryBreadcrumb?.collection
    const categoryKey = categoryBreadcrumb?.key
    const categoryText = categoryKey
      ? breadcrumbTranslations.category[categoryKey as keyof typeof breadcrumbTranslations.category]
      : undefined

    const supercategoryBreadcrumb = categoryBreadcrumb?.supercategory
    const superCategoryLink = supercategoryBreadcrumb?.collection
    const supercategoryKey = supercategoryBreadcrumb?.key
    const superCategoryText = supercategoryKey
      ? breadcrumbTranslations.supercategory[
          supercategoryKey as keyof typeof breadcrumbTranslations.supercategory
        ]
      : undefined

    return {
      links: {
        gender: genderLink,
        superCategory: superCategoryLink,
        category: categoryLink,
      },
      text: {
        gender: genderText,
        superCategory: superCategoryText,
        category: categoryText,
      },
    }
  }, [genderCategory, categoryId, navGenderCookie, breadcrumbTranslations])
}

export const PageBreadcrumbs: React.FC<UseBreadcrumbDataParams> = ({
  genderCategory: genderCategoryProp,
  categoryId: categoryProp,
}) => {
  const translations = useTranslation()

  const { links, text } = useBreadcrumbData({
    genderCategory: genderCategoryProp,
    categoryId: categoryProp,
  })

  return (
    <Wrapper>
      <Breadcrumbs aria-label={translations.pages.product.breadcrumbs.label}>
        {links.gender && text.gender && (
          <Breadcrumb href={links.gender} as={NextLink}>
            {text.gender}
          </Breadcrumb>
        )}
        {links.superCategory && text.superCategory && (
          <Breadcrumb as={NextLink} href={links.superCategory}>
            {text.superCategory}
          </Breadcrumb>
        )}
        {links.category && text.category && (
          <Breadcrumb href={links.category} as={NextLink}>
            {text.category}
          </Breadcrumb>
        )}
      </Breadcrumbs>
    </Wrapper>
  )
}

// STYLES
const Wrapper = styled.div`
  padding: ${props => props.theme.spacing(3, 4)};
  ${fromMd} {
    padding: ${props => props.theme.spacing(2.5, 0)};
  }
`
